import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Factory, 
  Box, 
  Cog, 
  Calendar, 
  Link as LinkIcon, 
  ArrowRight,
  TrendingUp,
  Clock
} from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/dashboard/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const statCards = [
    { 
      title: 'Îlots', 
      value: stats?.ilots || 0, 
      icon: Box, 
      href: '/ilots',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    { 
      title: 'Machines', 
      value: stats?.machines || 0, 
      icon: Cog, 
      href: '/machines',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    { 
      title: 'Actions', 
      value: stats?.actions || 0, 
      icon: Calendar, 
      href: '/planning',
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    },
    { 
      title: 'Liaisons', 
      value: stats?.liaisons || 0, 
      icon: LinkIcon, 
      href: '/planning',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
  ];
  
  const quickActions = [
    { title: 'Vue Atelier', description: 'Visualiser la disposition graphique', href: '/atelier', icon: Factory },
    { title: 'Nouvel Îlot', description: 'Créer un îlot de production', href: '/ilots', icon: Box },
    { title: 'Nouvelle Machine', description: 'Ajouter une machine-outil', href: '/machines', icon: Cog },
    { title: 'Planification', description: 'Gérer les actions et liaisons', href: '/planning', icon: Calendar },
  ];
  
  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase">
            Tableau de Bord
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Vue d'ensemble de votre atelier
          </p>
        </div>
        <Button asChild className="btn-industrial rounded-sm" data-testid="view-workshop-btn">
          <Link to="/atelier">
            <Factory className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Vue Atelier
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.href} data-testid={`stat-card-${stat.title.toLowerCase()}`}>
              <Card className="stats-card border-2 rounded-sm hover:border-primary/50 transition-all cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="industrial-label mb-2">{stat.title}</p>
                      {loading ? (
                        <Skeleton className="h-10 w-16" />
                      ) : (
                        <p className="font-heading font-bold text-4xl tracking-tight">{stat.value}</p>
                      )}
                    </div>
                    <div className={`w-12 h-12 rounded-sm ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
      
      {/* Today's Actions */}
      <Card className="border-2 rounded-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className="font-heading font-semibold text-xl uppercase tracking-tight">
                Actions du Jour
              </CardTitle>
              <CardDescription className="font-mono text-xs uppercase tracking-wider">
                Planification pour aujourd'hui
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : stats?.today_actions > 0 ? (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="font-heading font-bold text-5xl tracking-tight text-primary">
                  {stats.today_actions}
                </p>
                <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
                  actions planifiées
                </p>
              </div>
              <Button asChild variant="outline" className="rounded-sm border-2" data-testid="view-planning-btn">
                <Link to="/planning">
                  Voir la planification
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
              <p className="text-muted-foreground font-mono text-sm uppercase tracking-wider">
                Aucune action planifiée pour aujourd'hui
              </p>
              <Button asChild variant="outline" className="mt-4 rounded-sm border-2" data-testid="add-action-btn">
                <Link to="/planning">Ajouter une action</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Quick Actions */}
      <div>
        <h2 className="font-heading font-semibold text-xl uppercase tracking-tight mb-4">
          Accès Rapide
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link key={action.title} to={action.href} data-testid={`quick-action-${action.title.toLowerCase().replace(/\s/g, '-')}`}>
                <Card className="border-2 rounded-sm hover:border-primary hover:shadow-[4px_4px_0px_0px_hsl(var(--primary)/0.2)] transition-all cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-foreground" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading font-semibold text-lg uppercase tracking-tight mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {action.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
